import { Chart } from "chart.js"
import { ForwardedRef, forwardRef, useMemo } from "react"
import { Bar } from "react-chartjs-2"
import ZoomPlugin from "chartjs-plugin-zoom"
import {
  fillMissingTimesteps,
  getChartData,
  getMeanResults,
  getMedianResults,
  getNodeTableRowsMean,
  getNodeTableRowsMedian,
  getchartOptions,
  isComparisonDataset,
} from "@features/results/Result/MultiNodeBarChartContainer/MultiNodeBarChart/helpers"
import { NodeResult, RunDto } from "@custom-types/modelRun"
import { GraphDataViewModel } from "@features/graph/graphViewModel"
import { useLocalStorage } from "usehooks-ts"
import {
  STACKED_BAR_MULTI_NODE_CHART_OPTIONS_KEY,
  StackedBarChartOptions,
  defaultOptions,
} from "@features/results/Result/MultiNodeBarChartContainer/ChartOptionsDropdown"
import NodeResultsTable, {
  NodeResultRow,
} from "@features/results/Result/ChartsContainer/NodeResultsTable"
import { getNodeRunsTimesteps } from "@utils/run"

type MultiNodeBarChartProps = {
  title: string | undefined
  nodeResults: NodeResult[][]
  compareToResult: NodeResult[] | undefined
  yAxisUnitFormat: string | undefined
  runs: RunDto[]
  lag: number
  chartOptions?: StackedBarChartOptions
  maintainAspectRatio?: boolean
  customLabels?: string[]
  legendPosition?: "top" | "bottom"
  showAsTable?: boolean
  showLegend?: boolean
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
}

export type MeanDataSetsType = {
  dev1_low: Record<string, number>
  dev2_low: Record<string, number>
  dev3_low: Record<string, number>
  dev1_up: Record<string, number>
  dev2_up: Record<string, number>
  dev3_up: Record<string, number>
  mean: Record<string, number>
}

export type MedianDataSetsType = {
  lower_percentile: Record<string, number>
  median: Record<string, number>
  upper_percentile: Record<string, number>
}

Chart.register(ZoomPlugin)

const MultiNodeBarChart = forwardRef(function MultiNodeBarChart(
  props: MultiNodeBarChartProps,
  ref: ForwardedRef<Chart<"bar"> | undefined>,
) {
  const [chartDropdownOptions] = useLocalStorage<StackedBarChartOptions>(
    STACKED_BAR_MULTI_NODE_CHART_OPTIONS_KEY,
    defaultOptions,
  )

  const options = props.chartOptions ?? chartDropdownOptions

  const chartData = useMemo(
    () =>
      getChartData(
        props.nodeResults,
        props.compareToResult,
        props.lag,
        options,
        props.customLabels,
        props.runs,
      ),
    [props.nodeResults, props.compareToResult, props.customLabels, options, props.lag, props.runs],
  )

  // Apply hidden state to datasets based on hiddenDatasets prop
  if (props.hiddenDatasets) {
    chartData.datasets.forEach((dataset) => {
      if (dataset.label) {
        dataset.hidden = props.hiddenDatasets!.includes(dataset.label)
      }
    })
  }

  const hasComparison = chartData.datasets.some(isComparisonDataset)

  const barOptions = {
    ...getchartOptions(
      props.yAxisUnitFormat,
      props.legendPosition,
      props.showLegend,
      props.onLegendClick,
      options.isStacked,
      options.showAsPercentage,
      hasComparison,
    ),
    maintainAspectRatio: props.maintainAspectRatio ?? true,
  }

  if (!props.showAsTable) {
    return <Bar options={barOptions} data={chartData} ref={ref} />
  }

  const timesteps = getNodeRunsTimesteps(props.nodeResults, props.lag)
  let tableRows: NodeResultRow[] = []

  if (options.isMean) {
    const results = getMeanResults(props.nodeResults, props.lag, options.showStDev)
    const filledResults = fillMissingTimesteps(results, timesteps)

    let filledCompareRunResult: MeanDataSetsType | null = null
    if (props.compareToResult) {
      const compareResults = getMeanResults([props.compareToResult], props.lag, options.showStDev)
      filledCompareRunResult = fillMissingTimesteps(compareResults, timesteps)?.[0] ?? null
    }

    tableRows = getNodeTableRowsMean(
      filledResults,
      props.nodeResults,
      filledCompareRunResult,
      props.compareToResult ?? null,
      // TODO: support multiple runs in table view (#577736)
      props.runs[0],
      GraphDataViewModel.groupLabels,
      options.showStDev,
    )
  } else {
    const results = getMedianResults(props.nodeResults, props.lag, options)
    const filledResults = fillMissingTimesteps(results, timesteps)
    const percentiles = {
      lower: Number(options.percentileLowerValue ?? 0),
      upper: Number(options.percentileUpperValue ?? 0),
    }

    let filledCompareRunResult: MedianDataSetsType | null = null
    if (props.compareToResult) {
      const compareResults = getMedianResults([props.compareToResult], props.lag, options)
      filledCompareRunResult = fillMissingTimesteps(compareResults, timesteps)?.[0] ?? null
    }

    tableRows = getNodeTableRowsMedian(
      filledResults,
      props.nodeResults,
      props.yAxisUnitFormat,
      filledCompareRunResult,
      props.compareToResult ?? null,
      // TODO: support multiple runs in table view (#577736)
      props.runs[0],
      GraphDataViewModel.groupLabels,
      percentiles,
    )
  }

  return <NodeResultsTable rows={tableRows} exportFileName={props.title} />
})

export default MultiNodeBarChart
